import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { IRackEditComponentProps } from "./RackEditComponent.props";
import { useDispatch } from "react-redux";
import { makeNotifs } from "@utils/makeNotifs";
import {
  IRackSection,
  RACK_SECTION_TYPE,
  TypePartialRackItem,
} from "@components/Pages/Locations/LocationRow/LocationMore/RackTable/Rack/Rack.props";
import {
  addAvailableRack,
  editAvailableRack,
} from "@store/availableLocationRacks/actions";
import { ErrorType } from "@components/global/new/Error/Error";
import useApiRequests from "@services/http/useApiRequest";
import {
  AddRackTemplate,
  AddTemplateResponse,
  EditRack,
  EditRackResponse,
  EditRackTemplate,
} from "@services/rackService";
import { IAvailableRackItem } from "@store/availableLocationRacks/actionTypes";

import { compareMinersPositions } from "../helpers";
import { xyPositionRackItem } from "@utils/xyPositionRackItem";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";

export interface ISection extends Omit<IRackSection, "id"> {
  readonly id?: number;
}

interface IRackEditComponentControl {
  readonly isEdit: boolean;
  readonly title: string;
  readonly rackSections: ISection[];
  readonly minersPositions: ISection[];
  readonly err?: ErrorType;
  readonly loading: boolean;

  onSelectSection(num: number, position: [number, number]): void;
  isActive(num: number): boolean;
  onCreateOrEdit(): void;
}

export const useRackEditComponentControl = (
  props: IRackEditComponentProps
): IRackEditComponentControl => {
  const isEditRack = !!props.availableRack || !!props.rack;
  const dispatch = useDispatch();
  const { errors, ok } = makeNotifs();
  const { onError, onSuccess } = useGlobalNotifs();
  const { enqueue, isLoading } = useApiRequests();
  const [minersPositions, setMinersPositions] = useState<ISection[]>([]);

  const [rackSections, setRackSections] = useState<ISection[]>([]);
  const [err, setErr] = useState<ErrorType>();

  useEffect(() => {
    if (props.rackTypeError) {
      setErr({ ownError: [errors.locations__edit_rack] });
    } else {
      setErr(undefined);
    }
  }, [props.rackTypeError]);

  useLayoutEffect(() => {
    const miners: ISection[] = [];
    props.rack?.rack_items?.forEach((item, i) => {
      if (item.pos === RACK_SECTION_TYPE.MINER) {
        miners.push(item);
      }
    });
    setMinersPositions(miners);
  }, [props.rack?.rack_items]);

  const fillRackSections = (
    arr?: (TypePartialRackItem | IAvailableRackItem)[]
  ): ISection[] => {
    const fullLength = props.type[0] * props.type[1];
    if (!arr) {
      return Array.from(new Array(fullLength)).fill({
        pos: RACK_SECTION_TYPE.EMPTY,
      });
    }

    let length = fullLength - arr.length;

    let items: ISection[] = (arr as TypePartialRackItem[]).map((item) => {
      return { id: item.id, pos: item.pos, x: item.x, y: item.y };
    });

    if (length < 0) {
      items = items.slice(0, items.length + length);
      length = 0;
    }

    const joinedArr: ISection[] = Array.from(new Array(length)).fill({
      pos: RACK_SECTION_TYPE.EMPTY,
      // x: 0,
      // y: 0,
    });

    let itemsWithNewXY = [...items, ...joinedArr].map((item, i) => {
      const [X, Y] = xyPositionRackItem(i, props.type);
      return { ...item, x: X, y: Y };
    });

    // возвращаем верные позиции майнерам
    if (minersPositions.length > 0) {
      itemsWithNewXY = compareMinersPositions(
        minersPositions,
        itemsWithNewXY,
        props.type
      );
    }
    return itemsWithNewXY;
  };

  useEffect(() => {
    // if (firstRender) return;
    setRackSections(
      fillRackSections(
        props.rack?.rack_items || props.availableRack?.rack_items
      )
    );
  }, [props.type]);

  useEffect(() => {
    if (props.rackTypeError) return;
    const t = setTimeout(() => setErr(undefined), 2000);
    return () => clearTimeout(t);
  }, [err]);

  const isActive = useCallback(
    (num: number) => rackSections[num]?.pos !== RACK_SECTION_TYPE.NULL,
    [rackSections]
  );

  const onSelectSection = (num: number, position: [number, number]) => {
    const changedSection = {
      pos: RACK_SECTION_TYPE.EMPTY,
      x: +position[0],
      y: +position[1],
    };

    switch (rackSections[num].pos) {
      case RACK_SECTION_TYPE.EMPTY:
        changedSection.pos = RACK_SECTION_TYPE.NULL;
        break;
      case RACK_SECTION_TYPE.NULL:
        changedSection.pos = RACK_SECTION_TYPE.EMPTY;
        break;
      case RACK_SECTION_TYPE.MINER:
        setErr({ ownError: [errors.locations__edit_rack] });
        return;
    }

    setRackSections((prev) => [
      ...prev.slice(0, num),
      changedSection,
      ...prev.slice(num + 1),
    ]);
  };

  const makeTitle = useMemo((): string => {
    const filledSections = rackSections.filter(
      (item) => item.pos !== RACK_SECTION_TYPE.NULL
    );
    if (filledSections.length === 0) {
      return "Choose miners sections";
    }

    const height = props.type[1];
    const width = props.type[0];

    return `${height}x${width} (${filledSections.length.toString()} sections)`;
  }, [rackSections]);

  const onEditRack = async (width: number, height: number) => {
    if (!props.rack) return;
    const type = `${width},${height}`;
    const items = JSON.stringify(
      rackSections.map((item, i) => {
        return { pos: item.pos, x: item.x, y: item.y };
      })
    );
    const res = await enqueue<EditRackResponse>(
      new EditRack({
        id: props.rack.id,
        rack_items: items,
        rack_type: type,
      })
    );
    if (!res) return;
    if (props.update) props.update(res.Record.rack_items, [width, height]);
    onSuccess(ok.success);
    props.onClose();
  };

  const onCreateRackTemplate = async (width: number, height: number) => {
    const items = rackSections.map((r) => r.pos).join(",");
    const res = await enqueue<AddTemplateResponse>(
      new AddRackTemplate({
        rack_items: items,
        rack_type: [width, height].join(","),
        // object_id: props.building.id,
        // location_id: props.locationId.id,
      })
    );
    if (!res) return;

    dispatch(
      addAvailableRack({
        id: res.Record,
        rack_type: [width, height],
        rack_items: rackSections as unknown as IAvailableRackItem[],
      })
    );
    onSuccess(ok.success);
    props.onClose();
  };
  const onEditRackTemplate = async (width: number, height: number) => {
    if (!props.availableRack) return;
    const items = rackSections.map((r) => r.pos).join(",");
    const res = await enqueue<AddTemplateResponse>(
      new EditRackTemplate({
        id: props.availableRack.id,
        rack_items: items,
        rack_type: `${width},${height}`,
      })
    );
    if (!res) return;

    dispatch(
      editAvailableRack({
        id: props.availableRack.id,
        rack_items: rackSections as unknown as IAvailableRackItem[],
        rack_type: [width, height],
      })
    );
    onSuccess(ok.success);
    props.onClose();
  };

  const onCreateOrEdit = () => {
    const height = props.type[1];
    const width = props.type[0];
    const filledSections = rackSections.filter(
      (item) => item.pos !== RACK_SECTION_TYPE.NULL
    );

    if (filledSections.length === 0) {
      onError(errors.locations_select_rack_section);
      return;
    }

    if (!props.availableRack && !props.rack) {
      onCreateRackTemplate(width, height);
      return;
    }

    if (!!props.availableRack) {
      onEditRackTemplate(width, height);
      return;
    }
    onEditRack(width, height);
  };

  return {
    isEdit: isEditRack,
    title: makeTitle,
    rackSections,
    minersPositions,
    err,
    loading: isLoading,

    onSelectSection,
    isActive,
    onCreateOrEdit,
  };
};
